import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import {
  ApiActions,
  UpdateApiIsLoadingAction,
} from '@app/finxone-web-frontend/app/lib/state/api-loading.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  AlertTypes,
  ConfirmationPreferenceTypes,
  CredentialType,
  getConfirmationPreferenceLabel,
  getCredentialTypeFromConfirmationPreference,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Observable, Subject, filter, take, takeUntil } from 'rxjs';
import { KeycloakCustomService } from '../../../../lib/services/keycloak-wrapper-service/keycloak-custom.service';
import { ConfirmationRequest } from '../../../../lib/services/keycloak-wrapper-service/keycloak-custom.type';
import { LocaleVariableState } from '../../../../lib/state/locale-variable.state';
import { getConfirmationLocaleVariant } from '../utils/get-confirmation-locale-values';

type ConfirmationQueryParams = {
  claimType: string;
  confirmationType: CredentialType;
  returnUrl: string;
  addTokenToUrl?: boolean;
  loadingAction?: ApiActions;
  failureNavigationUrl?: string;
};

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-confirmation-code',
  templateUrl: './confirmation-code.component.html',
  styleUrl: './confirmation-code.component.scss',
})
export class ConfirmationCodeComponent implements OnInit, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  public localeContent$: Observable<any>;
  public localeContent: any;

  private claim: string | undefined = '';
  public confirmation: string | undefined = '';
  private returnUrl: string | undefined = '';
  private flutterWebView = (window as any).flutter_inappwebview;

  public pin = '';
  public error = '';
  public isMobile = false;
  public companyLogo?: string;
  public logo?: string;
  public readonly MAX_LENGTH: number = 6;
  public topPadding = '0px';
  public bottomPadding = '0px';
  public isDisableResendBtn = false;
  public localeValues: {
    title: string;
    subtitle: string;
    resendFrontText?: string;
    resendFrontTimerText?: string;
    resendBackText?: string;
    resendLinkText?: string;
    doBack?: string;
    doSubmit?: string;
    forgotPassword?: string;
    doTryAnotherWay?: string;
    codeExpired?: string;
    codeInvalid?: string;
  };
  public tryAnotherWay = false;
  public availableConfirmationMethods: ConfirmationPreferenceTypes[];
  private addTokenToUrl = false;
  public loadingAction?: ApiActions;
  private timeoutRef: any = null;
  private failureNavigationUrl?: string;

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private keycloakService: KeycloakCustomService,
    private matIconRegistry: MatIconRegistry,
    private configService: ConfigService,
    private alertHandlerService: AlertHandlerService,
  ) {
    this.localeContent$ = this.store.select(LocaleVariableState.getLocaleContent);
    this.matIconRegistry
      .getNamedSvgIcon('customer-logo')
      .pipe(take(1))
      .subscribe((logo) => {
        this.companyLogo = this.resizeLogo(logo.outerHTML);
      });
    // This is the in-app padding needed for the safe area on mobile devices
    this.configService
      .getSafePadding()
      .pipe(take(1))
      .subscribe((padding) => {
        if (padding.isIOS) {
          // add padding on ios
          this.topPadding = `${padding.topPadding}px`;
          this.bottomPadding = `${padding.bottomPadding}px`;
        }
      });

    this.availableConfirmationMethods = this.store.selectSnapshot(
      ProfileState.getAvailableConfirmationMethods,
    );
  }

  ngOnDestroy(): void {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
    }
  }

  ngOnInit(): void {
    this.timeoutRef = setTimeout(() => {
      this.alertHandlerService.showAlertFn(AlertTypes.INFO, 'Confirmation has timed out, please try again.');
      this.location.back();

      // Auto-cancel confirmation code after 2 minutes
    }, 120000);

    document.getElementById('loader')?.remove();
    this.isMobile = window.innerWidth <= 768;

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: Partial<ConfirmationQueryParams>) => {
        this.claim = params['claimType'];
        this.confirmation = params['confirmationType'];
        this.returnUrl = params['returnUrl'] || '/';
        this.failureNavigationUrl = params?.['failureNavigationUrl'] || '';
        //weird conversion as param is set via boolean, but when it gets it from the params it turns it into a string
        this.addTokenToUrl =
          params['addTokenToUrl'] && params['addTokenToUrl'].toString() === 'true' ? true : false; // NOSONAR
        this.loadingAction = params['loadingAction'] ?? undefined;
        if (!this.claim || !this.confirmation) {
          console.error('Missing required confirmation parameters');
          this.router.navigate(['/']).then();
        }
      });

    this.localeContent$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (content) => {
        this.localeContent = content;
        this.localeValues = getConfirmationLocaleVariant(
          content,
          this.confirmation as CredentialType,
          this.tryAnotherWay,
        ); // Might need this to pull out error messages
      },
      error: (error) => {
        console.error('Error loading content:', error);
      },
    });
  }

  public checkPinComplete(): void {
    if (this.pin.length === this.MAX_LENGTH && /^\d+$/.test(this.pin)) {
      setTimeout(() => this.onSubmit(), 200);
    }
  }

  public async onBiometricChallenge(challenge: string): Promise<void> {
    this.confirmation = CredentialType.BIOMETRIC;
    try {
      if (this.flutterWebView) {
        const { signature } = await this.flutterWebView.callHandler('signChallengeWithBiometric', [
          challenge,
        ]);
        const payload: ConfirmationRequest = this.validateAndCreatePayload(signature);

        this.handleConfirmation(payload);
      }
    } catch (error) {
      console.error('Biometric authentication failed:', error);
      this.error = 'Biometric authentication failed. Please try again or use PIN.';
    }
  }

  private async onSubmit(): Promise<void> {
    if (this.confirmation === CredentialType.BIOMETRIC) {
      this.confirmation = CredentialType.PIN; // in case someone tries biometrics, then PIN again
    }
    try {
      const payload: ConfirmationRequest = this.validateAndCreatePayload(this.pin);
      await this.handleConfirmation(payload);
    } catch (error) {
      console.error(error);
      // Placeholder - var could be populated via locale content
      this.error = 'Invalid passcode, please try again';
      this.pin = '';
    }
  }

  private async handleConfirmation(payload: ConfirmationRequest) {
    await this.keycloakService
      .sendConfirmation(payload)
      .then(({ access_token }) => {
        if (this.returnUrl !== '/') {
          if (this.addTokenToUrl) {
            const hasQueryParams = this.returnUrl?.includes('?');
            const queryParams: URLSearchParams = new URLSearchParams();
            queryParams.set('confirmation', access_token);
            queryParams.set('claimType', this.claim ?? '');
            // Construct the final URL
            const fullUrl = `${this.returnUrl}${hasQueryParams ? '&' : '?'}${queryParams?.toString()}`;
            this.router.navigateByUrl(fullUrl, { replaceUrl: true });
          } else {
            this.router.navigateByUrl(this.returnUrl ?? '/', { replaceUrl: true });
          }
        }
        if (this.loadingAction) {
          if (this.addTokenToUrl) {
            const url = this.router.url.split('?')[0];
            const queryParams = new URLSearchParams(window.location.search);
            queryParams.set('confirmation', access_token);
            queryParams.set('claimType', this.claim ?? '');
            this.location.replaceState(url, queryParams.toString());
          }
          this.store.dispatch(new UpdateApiIsLoadingAction(this.loadingAction, true));
        }
      })
      .catch((error) => {
        console.error('Error during confirmation:', error);
        // failureNavigationUrl is optional, so only navigate if it's provided
        if (this.failureNavigationUrl) {
          this.router.navigateByUrl(this.failureNavigationUrl, { replaceUrl: true });
        }
        throw error;
      });
  }

  private validateAndCreatePayload(value: string): ConfirmationRequest {
    if (!this.claim?.length || !this.confirmation?.length) {
      throw new Error('Missing required fields for confirmation payload');
    }
    return {
      claim: this.claim,
      factors: [
        {
          type: this.confirmation,
          value: value,
        },
      ],
    };
  }

  onAltFlowClick(event: Event): void {
    this.tryAnotherWay = true;
    this.localeValues = getConfirmationLocaleVariant(
      this.localeContent,
      this.confirmation as CredentialType,
      this.tryAnotherWay,
    );
    event.preventDefault();
  }

  onBackClick(event: Event): void {
    event.preventDefault();
    this.location.back();
    if (this.loadingAction) {
      this.store.dispatch(new UpdateApiIsLoadingAction(this.loadingAction, false));
    }
    //since we are requiring an auth per action, remove the confirmation token from the URL if you've authed, attempt to reauth but then click back
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('confirmation');
        this.router.navigateByUrl(url.pathname + url.search, { replaceUrl: true });
      });
  }

  private resizeLogo(svg?: string): string | undefined {
    let processedLogo = svg ? svg.replace(/(height=["'])[^"']*["']/, `$185px"`) : undefined;
    // If no height exists, add it
    if (processedLogo && !processedLogo.includes('height=')) {
      processedLogo = processedLogo.replace('<svg', '<svg height="85px" ');
    }

    processedLogo = processedLogo ? processedLogo.replace(/(width=["'])[^"']*["']/, `$1100%"`) : undefined;
    // If no height exists, add it
    if (processedLogo && !processedLogo.includes('width=')) {
      processedLogo = processedLogo.replace('<svg', '<svg width="100%" ');
    }
    return processedLogo;
  }

  public isConfirmationTypeOTP(): boolean {
    return (
      this.confirmation === CredentialType.SMS ||
      this.confirmation === CredentialType.EMAIL ||
      this.confirmation === CredentialType.TOTP
    );
  }

  public getConfirmationTypeLabel(type: ConfirmationPreferenceTypes): string {
    return getConfirmationPreferenceLabel(type);
  }

  public selectAlternateMethod(type: ConfirmationPreferenceTypes): void {
    this.confirmation = getCredentialTypeFromConfirmationPreference(type);
    this.tryAnotherWay = false;
    this.localeValues = getConfirmationLocaleVariant(
      this.localeContent,
      this.confirmation as CredentialType,
      this.tryAnotherWay,
    );
    this.pin = '';
    this.error = '';
  }

  public backFromAlternateMethod(): void {
    this.tryAnotherWay = false;
    this.localeValues = getConfirmationLocaleVariant(
      this.localeContent,
      this.confirmation as CredentialType,
      this.tryAnotherWay,
    );
  }
}
